import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { BedDouble, Plus, RefreshCw, Trash2, LogOut as LogOutIcon, Receipt, FileText, ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Portal from "@/components/Portal";
import DischargeSummary from "@/components/DischargeSummary";
import PurgeDataButton from "@/components/PurgeDataButton";

export default function ManagerIP() {
  const { branch } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("admissions");
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [chargeFor, setChargeFor] = useState(null);
  const [summaryFor, setSummaryFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get("/rooms", { params: { branch_code: branch.code } }),
        api.get("/admissions", { params: { branch_code: branch.code } }),
      ]);
      setRooms(r.data);
      setAdmissions(a.data);
    } catch (err) {
      toast.error(formatApiError(err, "Load failed"));
    } finally {
      setLoading(false);
    }
  }, [branch?.code]);

  useEffect(() => { load(); }, [load]);

  const deleteRoom = async (id) => {
    try {
      await api.delete(`/rooms/${id}`);
      toast.success("Room deleted");
      load();
    } catch (err) { toast.error(formatApiError(err, "Delete failed")); }
  };

  const discharge = async (id) => {
    try {
      const { data } = await api.post(`/admissions/${id}/discharge`);
      toast.success("Patient discharged");
      setSummaryFor(data);
      load();
    } catch (err) { toast.error(formatApiError(err, "Discharge failed")); }
  };

  const vacantRooms = rooms.filter((r) => r.status !== "occupied");

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="manager-ip-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Manager · IP · {branch.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">In-patients &amp; rooms</h1>
          <p className="text-[#5C5652]">Admit, add running charges, and discharge — bills tally daily tariff + line items.</p>
        </div>
        <button onClick={load} className="h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2" data-testid="ip-refresh">
          <RefreshCw className="h-4 w-4" strokeWidth={1.75} /> Refresh
        </button>
        <PurgeDataButton collection="admissions" label="admissions" onDone={load} />
      </div>

      <div className="flex bg-[#F9F6F0] border border-sand rounded-lg p-1 mb-6 w-fit">
        {[
          { v: "admissions", l: `Admissions (${admissions.length})` },
          { v: "rooms", l: `Rooms (${rooms.length})` },
        ].map((t) => (
          <button
            key={t.v} onClick={() => setTab(t.v)} data-testid={`tab-${t.v}`}
            className={`px-4 h-9 rounded-md text-sm font-semibold ${tab === t.v ? "bg-forest text-white" : "text-[#5C5652]"}`}
          >{t.l}</button>
        ))}
      </div>

      {tab === "admissions" && (
        <>
          <div className="mb-4 flex justify-end">
            <button onClick={() => setAdmitOpen(true)} data-testid="admit-new" disabled={vacantRooms.length === 0}
              className="h-10 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2 disabled:opacity-60">
              <Plus className="h-4 w-4" strokeWidth={1.75} /> Admit patient
            </button>
          </div>
          <div className="bg-white border border-sand rounded-lg overflow-hidden">
            {loading ? <div className="p-8 text-sm text-[#5C5652]">Loading…</div>
              : admissions.length === 0 ? <div className="p-10 text-center text-sm text-[#5C5652]">No in-patients right now.</div>
              : (
                <ul className="divide-y divide-[#E8E3D9]" data-testid="admissions-list">
                  {admissions.map((a) => (
                    <li key={a.id} className="px-6 py-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center">
                      <div>
                        <div className="font-semibold text-[#2A2624]">{a.patient_name} <span className="text-xs text-[#5C5652] ml-2">{a.admission_no} · {a.mrn}</span></div>
                        <div className="text-xs text-[#5C5652]">Room {a.room_number} · {a.room_type} · ₹ {Number(a.tariff_per_day).toFixed(0)}/day · Day {a.days}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Grand total</div>
                        <div className="font-display text-lg text-forest">₹ {a.grand_total.toFixed(2)}</div>
                      </div>
                      <Link to={`/manager/ip/chart/${a.id}`} data-testid={`open-chart-${a.admission_no}`}
                        className="h-9 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2">
                        <ClipboardList className="h-4 w-4" strokeWidth={1.75} /> Chart
                      </Link>
                      <button onClick={() => setChargeFor(a)} data-testid={`add-charge-${a.admission_no}`}
                        className="h-9 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2">
                        <Receipt className="h-4 w-4" strokeWidth={1.75} /> Add charge
                      </button>
                      <button onClick={() => discharge(a.id)} data-testid={`discharge-${a.admission_no}`}
                        className="h-9 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2">
                        <LogOutIcon className="h-4 w-4" strokeWidth={1.75} /> Discharge
                      </button>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </>
      )}

      {tab === "rooms" && (
        <>
          <div className="mb-4 flex justify-end">
            <button onClick={() => setAddRoomOpen(true)} data-testid="add-room"
              className="h-10 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2">
              <Plus className="h-4 w-4" strokeWidth={1.75} /> Add room
            </button>
          </div>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="rooms-grid">
            {rooms.map((r) => (
              <div key={r.id} className="p-5 rounded-lg bg-white border border-sand">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">{r.room_type}</div>
                    <div className="font-display text-2xl text-forest">Room {r.room_number}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider ${r.status === "occupied" ? "bg-[#E5A93D]/15 text-[#B57F1D]" : "bg-[#1A4331]/10 text-forest"}`}>
                    {r.status}
                  </span>
                </div>
                <div className="text-sm text-[#5C5652] mt-3">₹ {Number(r.tariff_per_day).toFixed(0)} / day</div>
                {r.status !== "occupied" && (
                  <button onClick={() => deleteRoom(r.id)} data-testid={`delete-room-${r.room_number}`}
                    className="mt-4 text-xs text-[#C25946] hover:underline inline-flex items-center gap-1">
                    <Trash2 className="h-3 w-3" strokeWidth={2} /> Delete
                  </button>
                )}
              </div>
            ))}
            {rooms.length === 0 && !loading && (
              <div className="col-span-full text-center p-10 bg-white border border-sand rounded-lg text-sm text-[#5C5652]">
                No rooms yet — add your first room.
              </div>
            )}
          </div>
        </>
      )}

      {addRoomOpen && <AddRoomModal branch={branch} onClose={() => setAddRoomOpen(false)} onSaved={() => { setAddRoomOpen(false); load(); }} />}
      {admitOpen && <AdmitModal branch={branch} rooms={vacantRooms} onClose={() => setAdmitOpen(false)} onSaved={() => { setAdmitOpen(false); load(); }} />}
      {chargeFor && <ChargeModal admission={chargeFor} onClose={() => setChargeFor(null)} onSaved={() => { setChargeFor(null); load(); }} />}
      {summaryFor && <DischargeSummary admission={summaryFor} branchName={branch.name} onClose={() => setSummaryFor(null)} />}
    </div>
  );
}

function AddRoomModal({ branch, onClose, onSaved }) {
  const [form, setForm] = useState({ room_number: "", room_type: "General", tariff_per_day: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.room_number.trim() || !form.tariff_per_day) { toast.error("Fill number and tariff"); return; }
    setBusy(true);
    try {
      await api.post("/rooms", { branch_code: branch.code, room_number: form.room_number.trim(),
        room_type: form.room_type, tariff_per_day: Number(form.tariff_per_day) });
      toast.success("Room added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Portal>
    <div className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg border border-sand p-6" onClick={(e) => e.stopPropagation()} data-testid="add-room-modal">
        <div className="font-display text-2xl text-forest mb-4">Add room · {branch.name}</div>
        <div className="space-y-3">
          <input data-testid="room-number" value={form.room_number} onChange={(e) => setForm(f => ({...f, room_number: e.target.value}))} placeholder="Room number (e.g. R101)"
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <select data-testid="room-type" value={form.room_type} onChange={(e) => setForm(f => ({...f, room_type: e.target.value}))}
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none bg-white">
            {["General","Semi-private","Private","Deluxe","Panchakarma"].map(t => <option key={t}>{t}</option>)}
          </select>
          <input data-testid="room-tariff" type="number" min="0" value={form.tariff_per_day} onChange={(e) => setForm(f => ({...f, tariff_per_day: e.target.value}))} placeholder="Tariff per day (₹)"
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} disabled={busy} data-testid="save-room" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60">Save room</button>
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function AdmitModal({ branch, rooms, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [roomId, setRoomId] = useState(rooms[0]?.id || "");
  const [deposit, setDeposit] = useState("0");
  const [reason, setReason] = useState("");
  const [insuranceClaim, setInsuranceClaim] = useState(false);
  const [insuranceProvider, setInsuranceProvider] = useState("");
  const [insurancePolicy, setInsurancePolicy] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients", { params: { branch_code: branch.code, q } });
        setResults(data.slice(0, 8));
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, branch.code]);

  const admit = async () => {
    if (!patient || !roomId) { toast.error("Pick a patient and a room"); return; }
    setBusy(true);
    try {
      await api.post("/admissions", {
        patient_id: patient.id, room_id: roomId, deposit: Number(deposit) || 0, reason,
        insurance_claim: insuranceClaim,
        insurance_provider: insuranceProvider.trim(),
        insurance_policy_no: insurancePolicy.trim(),
      });
      toast.success("Admitted");
      onSaved();
    } catch (err) { toast.error(formatApiError(err, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <Portal>
    <div className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-lg border border-sand p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admit-modal">
        <div className="font-display text-2xl text-forest mb-4">Admit patient</div>
        <div className="space-y-3">
          {!patient ? (
            <>
              <input data-testid="admit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient by name, phone or MRN"
                className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
              <div className="max-h-60 overflow-y-auto border border-sand rounded-lg divide-y divide-[#E8E3D9]">
                {results.map(p => (
                  <button key={p.id} onClick={() => setPatient(p)} data-testid={`admit-pick-${p.mrn}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#F9F6F0]">
                    <div className="text-sm font-semibold text-[#2A2624]">{p.name}</div>
                    <div className="text-xs text-[#5C5652]">{p.mrn} · {p.phone}</div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="p-3 rounded-lg bg-[#F9F6F0] border border-sand flex items-center justify-between">
              <div>
                <div className="font-semibold">{patient.name}</div>
                <div className="text-xs text-[#5C5652]">{patient.mrn} · {patient.phone}</div>
              </div>
              <button onClick={() => setPatient(null)} className="text-xs text-forest font-semibold hover:underline">Change</button>
            </div>
          )}
          <select data-testid="admit-room" value={roomId} onChange={(e) => setRoomId(e.target.value)}
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none bg-white">
            <option value="">— Pick a vacant room —</option>
            {rooms.map(r => <option key={r.id} value={r.id}>{r.room_number} · {r.room_type} · ₹{r.tariff_per_day}/day</option>)}
          </select>
          <input data-testid="admit-deposit" type="number" min="0" value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="Deposit (₹)"
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="admit-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason / diagnosis"
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <label className="flex items-center gap-2 p-3 rounded-lg border border-sand bg-[#F9F6F0] cursor-pointer">
            <input type="checkbox" data-testid="admit-insurance" checked={insuranceClaim} onChange={(e) => setInsuranceClaim(e.target.checked)} className="h-4 w-4 accent-[#1A4331]" />
            <span className="text-sm font-semibold text-forest">Insurance claim admission</span>
          </label>
          {insuranceClaim && (
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="admit-ins-provider" value={insuranceProvider} onChange={(e) => setInsuranceProvider(e.target.value)} placeholder="Provider (e.g. Star Health)"
                className="h-10 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none text-sm" />
              <input data-testid="admit-ins-policy" value={insurancePolicy} onChange={(e) => setInsurancePolicy(e.target.value)} placeholder="Policy no."
                className="h-10 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none text-sm font-mono" />
            </div>
          )}
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={admit} disabled={busy || !patient || !roomId} data-testid="admit-submit" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60">Admit</button>
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function ChargeModal({ admission, onClose, onSaved }) {
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!label.trim() || !amount) { toast.error("Fill label and amount"); return; }
    setBusy(true);
    try {
      await api.post(`/admissions/${admission.id}/charge`, { label: label.trim(), amount: Number(amount) });
      toast.success("Charge added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Portal>
    <div className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg border border-sand p-6" onClick={(e) => e.stopPropagation()} data-testid="charge-modal">
        <div className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1">{admission.admission_no} · Room {admission.room_number}</div>
        <div className="font-display text-2xl text-forest mb-4">Add running charge</div>
        <div className="space-y-3">
          <input data-testid="charge-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g., Abhyanga therapy)"
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="charge-amount" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (₹)"
            className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          {admission.charges?.length > 0 && (
            <div className="text-xs bg-[#F9F6F0] border border-sand rounded p-3 max-h-40 overflow-y-auto">
              <div className="font-bold uppercase tracking-wider text-[#5C5652] mb-2">Existing charges</div>
              {admission.charges.map((c, i) => (
                <div key={i} className="flex justify-between py-1"><span>{c.label}</span><span>₹ {Number(c.amount).toFixed(2)}</span></div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} disabled={busy} data-testid="charge-save" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60">Add charge</button>
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
